import { instantSurveyGroupRefUrlVariable, QUESTION_LOGIC_OPERATORS } from './constants';
import type {
  Question,
  QuestionLogic,
  QuestionLogicOperator,
  QuestionLogicQuotaGroupRow,
} from '../types';

type SkipLogicRow = NonNullable<QuestionLogic['skip']>['logic'][number];

/**
 * Returns questions that can be shown for the selected quota group. Skip logic is
 * authored as "Ask this question if", so a satisfied quota row means the question
 * is visible for that quota group. Non-quota rows depend on respondent answers and
 * are treated as potentially satisfiable.
 */
export const getVisibleQuestionsForQuotaGroup = ({
  questionLogic,
  questions = [],
  quotaGroupId,
}: {
  questionLogic?: Record<string, QuestionLogic>;
  questions?: Question[];
  quotaGroupId?: number;
}) => {
  if (quotaGroupId == null || !questionLogic) return questions;

  return questions.filter((question) =>
    getIsQuestionVisibleForQuotaGroup({
      question,
      questionLogic,
      quotaGroupId,
    })
  );
};

const getIsQuestionVisibleForQuotaGroup = ({
  question,
  questionLogic,
  quotaGroupId,
}: {
  question: Question;
  questionLogic: Record<string, QuestionLogic>;
  quotaGroupId: number;
}) => {
  const skipLogic = questionLogic[question.id]?.skip;
  const logicRows = skipLogic?.logic ?? [];
  const quotaLogicRows = logicRows.filter(isQuotaGroupLogicRow);
  const hasNonQuotaLogicRows = logicRows.length > quotaLogicRows.length;

  if (!quotaLogicRows.length) return true;

  const quotaRowResults = quotaLogicRows.map((logic) =>
    getIsQuotaGroupLogicRowSatisfied({ logic, quotaGroupId })
  );

  if (getOperatorValue(skipLogic?.operator) === QUESTION_LOGIC_OPERATORS.ANY.value) {
    return hasNonQuotaLogicRows || quotaRowResults.some(Boolean);
  }

  return quotaRowResults.every(Boolean);
};

const isQuotaGroupLogicRow = (logic: SkipLogicRow): logic is QuestionLogicQuotaGroupRow => {
  return 'url_variable' in logic && logic.url_variable === instantSurveyGroupRefUrlVariable;
};

const getIsQuotaGroupLogicRowSatisfied = ({
  logic,
  quotaGroupId,
}: {
  logic: QuestionLogicQuotaGroupRow;
  quotaGroupId: number;
}) => {
  const quotaGroupIds = (logic.values ?? []).map(String);
  const isQuotaGroupIncluded = quotaGroupIds.includes(String(quotaGroupId));
  return getOperatorValue(logic.operator) === QUESTION_LOGIC_OPERATORS.NOT.value
    ? !isQuotaGroupIncluded
    : isQuotaGroupIncluded;
};

const getOperatorValue = (operator?: QuestionLogicOperator) => {
  if (typeof operator === 'string') return operator;
  return operator?.value;
};
